import styled, { css } from "styled-components";

export const GridWrapper = styled.div<{ $continuousRange?: boolean }>`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(6)};
  min-height: ${({ theme }) => theme.spacing(44)};
  row-gap: ${({ theme }) => theme.spacing(1)};
  column-gap: ${({ $continuousRange, theme }) =>
    $continuousRange ? 0 : theme.spacing(1)};
  grid-template-columns: repeat(7, 1fr);
  align-items: start;
  justify-items: ${({ $continuousRange }) =>
    $continuousRange ? "stretch" : "center"};
  display: grid;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing(2)};
    min-height: ${({ theme }) => theme.spacing(36)};
  }
`;

export const Header = styled.div<{ $continuousRange?: boolean }>`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.mediumGray};
  text-align: center;
  ${({ $continuousRange }) =>
    $continuousRange &&
    css`
      font-size: 0.8125rem;
      letter-spacing: 0.04em;
      padding-bottom: 0.4rem;
    `}
`;

export const Day = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "isSelectedDestination",
      "isSelectedOrigin",
      "isCurrentMonth",
      "isSelected",
      "isDisabled",
      "isGray",
      "isInRange",
      "isRangeMember",
      "isRangeStart",
      "isRangeEnd",
      "isRangeRowStart",
      "isRangeRowEnd",
    ].includes(prop),
})<{
  $continuousRange?: boolean;
  isSelectedDestination?: boolean;
  isSelectedOrigin?: boolean;
  isCurrentMonth: boolean;
  isDisabled?: boolean;
  isSelected: boolean;
  isGray?: boolean;
  isInRange?: boolean;
  isRangeMember?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  isRangeRowStart?: boolean;
  isRangeRowEnd?: boolean;
}>`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  width: ${({ $continuousRange, theme }) =>
    $continuousRange ? "100%" : theme.spacing(5)};
  height: ${({ theme }) => theme.spacing(5)};
  color: ${({
    isCurrentMonth,
    isDisabled,
    isGray,
    isSelected,
    $continuousRange,
    theme,
  }) =>
    $continuousRange && isSelected
      ? theme.colors.white
      : isDisabled || isGray
        ? theme.colors.lightGray
        : isCurrentMonth
          ? theme.colors.mediumGray
          : theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: background-color 0.2s ease-in-out;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: ${({ isSelected, isInRange }) =>
    isSelected ? 2 : isInRange ? 1 : 0};
  display: flex;
  cursor: ${({ isCurrentMonth, isDisabled, isGray }) =>
    (isCurrentMonth && !isDisabled) || isGray ? "pointer" : "default"};

  background: ${({
    isSelectedOrigin,
    isSelectedDestination,
    isSelected,
    isGray,
    $continuousRange,
    theme,
  }) =>
    isGray && !(isSelectedOrigin && isSelectedDestination)
      ? theme.colors.gray
      : isSelectedOrigin && isSelectedDestination
        ? `linear-gradient(
          225deg,
          ${theme.colors.yellow} 50%,
          ${theme.colors.vividBlue} 50%
        )`
        : isSelectedOrigin && isSelected
          ? theme.colors.yellow
          : isSelectedDestination && isSelected
            ? theme.colors.vividBlue
            : isSelected
              ? $continuousRange
                ? theme.colors.darkBlue
                : theme.colors.mediumBlue || "#b3d4fc"
              : "transparent"};

  &::before {
    content: "";
    position: absolute;
    z-index: 0;
    top: 0;
    bottom: 0;
    left: ${({ isInRange, isRangeRowStart }) =>
      isInRange && !isRangeRowStart ? "-1px" : 0};
    right: ${({ isInRange, isRangeRowEnd }) =>
      isInRange && !isRangeRowEnd ? "-1px" : 0};
    border-radius: ${({ isInRange, isRangeRowStart, isRangeRowEnd, theme }) =>
      isInRange && (isRangeRowStart || isRangeRowEnd)
        ? theme.borderRadius.small
        : 0};
    background-color: ${({ isInRange, theme }) =>
      isInRange
        ? `color-mix(in srgb, ${theme.colors.mediumBlue} 16%, ${theme.colors.white})`
        : "transparent"};
  }

  &:hover {
    background: ${({
      isSelectedOrigin,
      isSelectedDestination,
      isDisabled,
      isSelected,
      $continuousRange,
      theme,
    }) =>
      !isDisabled && isSelectedOrigin && isSelectedDestination
        ? `linear-gradient(
        225deg,
        ${theme.colors.yellow} 50%,
        ${theme.colors.vividBlue} 50%
      )`
        : !isDisabled && isSelectedDestination
          ? theme.colors.vividBlue
          : !isDisabled && isSelectedOrigin
            ? theme.colors.yellow
            : !isDisabled &&
              ($continuousRange
                ? isSelected
                  ? theme.colors.darkBlue
                  : theme.colors.lightBlue
                : theme.colors.lightGray)};
  }
`;

export const ContentDay = styled.div`
  width: ${({ theme }) => theme.spacing(5)};
  height: ${({ theme }) => theme.spacing(5)};
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  border-radius: inherit;
  background: inherit;
  display: flex;

  & > p {
    padding-top: 0.15rem;
  }
`;

export const IconDiv = styled.div`
  width: ${({ theme }) => theme.spacing(5)};
  height: ${({ theme }) => theme.spacing(5)};
  position: absolute;
  z-index: 1;

  & > svg {
    position: absolute;
    font-size: 0.65rem;
  }
`;

export const IconOrigin = styled(IconDiv)<{ isGray?: boolean }>`
  & > svg {
    background: transparent;
    /* background: ${({ theme, isGray }) =>
      isGray ? theme.colors.gray : theme.colors.yellow}; */
    right: 0;
    top: 0;
  }
`;

export const IconDestination = styled(IconDiv)<{ isGray?: boolean }>`
  & > svg {
    background: transparent;
    /* background: ${({ theme, isGray }) =>
      isGray ? theme.colors.gray : theme.colors.vividBlue}; */
    bottom: 0;
    left: 0;
  }
`;
